
/*
 * ONEFLOW STUDIO – Weiter einkaufen
 *
 * Betriebsarten:
 * AUS: Letzte Collection-/Suchergebnis-URL wiederherstellen.
 * EIN: Zusaetzlich die gespeicherte Scrollposition
 *      wiederherstellen, ggf. mit verdecktem Nachladen.
 *
 * Keine direkten Searchanise-Pagination-Aufrufe.
 */

(() => {
  'use strict';

  const URL_KEY = 'ofs_last_shopping_url';
  const SCROLL_KEY = 'ofs_last_shopping_scroll_y';
  const RESTORE_KEY = 'ofs_restore_shopping_scroll';

  const script = document.querySelector(
    'script[data-ofs-shopping-context]'
  );

  const pageType = script?.dataset.pageType;
  const isCollection = pageType === 'collection';

  const isResultsPage = () =>
    location.pathname === '/pages/search-results-page' &&
    Boolean(
      new URLSearchParams(location.search).get('q')?.trim()
    );

  const isShoppingPage = isCollection || isResultsPage();

  const isValidShoppingUrl = (value) => {
    try {
      const url = new URL(value);

      return (
        url.origin === location.origin &&
        (
          url.pathname.startsWith('/collections/') ||
          (
            url.pathname === '/pages/search-results-page' &&
            Boolean(url.searchParams.get('q')?.trim())
          )
        )
      );
    } catch (_) {
      return false;
    }
  };

  // --------------------------------------------------
  // COLLECTIONS UND SEARCHANISE-SUCHERGEBNISSE
  // --------------------------------------------------

  if (isShoppingPage) {

    const savedUrl = sessionStorage.getItem(URL_KEY);
    const targetY = Number(
      sessionStorage.getItem(SCROLL_KEY) || 0
    );

    const shouldRestore =
      sessionStorage.getItem(RESTORE_KEY) === 'true' &&
      savedUrl === location.href &&
      Number.isFinite(targetY) &&
      targetY > 0;

    let restoring = shouldRestore;
    let restoreTimer = null;
    let scrollTimer = null;
    let overlay = null;

    const saveCurrentUrl = () => {
      if (isCollection || isResultsPage()) {
        sessionStorage.setItem(URL_KEY, location.href);
      }
    };

    const saveScrollPosition = () => {
      if (!restoring) {
        sessionStorage.setItem(
          SCROLL_KEY,
          String(window.scrollY)
        );
      }
    };

    const maxScroll = () => {
      const height = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight
      );

      return Math.max(0, height - window.innerHeight);
    };

    const productCount = () =>
      document.querySelectorAll(
        '.snize-search-results-content .snize-product'
      ).length;

    // Saemtliche alten Restore-Marker konsumieren.
    sessionStorage.removeItem(RESTORE_KEY);

    // ------------------------------------------------
    // WIEDERHERSTELLUNG
    // ------------------------------------------------

    const removeOverlay = () => {
      if (overlay) {
        overlay.remove();
        overlay = null;
      }
    };

    const finishRestore = (success) => {
      if (!restoring) return;

      clearTimeout(restoreTimer);

      if (success) {
        window.scrollTo({
          top: targetY,
          left: 0,
          behavior: 'instant'
        });
      } else {
        // Bei Abbruch normale Seitennutzung ermoeglichen.
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'instant'
        });
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          restoring = false;
          removeOverlay();
          saveScrollPosition();
        });
      });
    };

    const showOverlay = () => {
      if (overlay || !restoring) return;

      overlay = document.createElement('div');
      overlay.id = 'ofs-scroll-restore-overlay';

      overlay.style.cssText = `
        position: fixed;
        inset: 0;
        z-index: 2147483647;
        background: #ffffff;
        color: #63461b;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 16px;
        padding: 24px;
        box-sizing: border-box;
        text-align: center;
        font-family: inherit;
      `;

      const message = document.createElement('div');
      message.textContent =
        'Deine Einkaufsauswahl wird wiederhergestellt …';

      message.style.cssText = `
        font-size: 18px;
        font-weight: 600;
      `;

      const detail = document.createElement('div');
      detail.textContent = 'Bitte einen Moment warten.';
      detail.style.fontSize = '14px';

      const cancel = document.createElement('button');
      cancel.type = 'button';
      cancel.textContent = 'Zur Ergebnisliste wechseln';

      cancel.style.cssText = `
        background: transparent;
        border: 1px solid #63461b;
        color: #63461b;
        padding: 12px 18px;
        cursor: pointer;
        font: inherit;
      `;

      cancel.addEventListener('click', () => {
        finishRestore(false);
      });

      overlay.append(message, detail, cancel);
      document.body.appendChild(overlay);
    };

    if (restoring) {
      const startedAt = Date.now();

      const MAX_DURATION = 20000;
      const STEP_SIZE = 400;
      const STEP_DELAY = 600;
      const INITIAL_DELAY = 500;

      const restoreStep = () => {
        if (!restoring) return;

        // Ziel bereits erreichbar?
        if (maxScroll() >= targetY) {
          finishRestore(true);
          return;
        }

        // Sicherheitsgrenze.
        if (Date.now() - startedAt >= MAX_DURATION) {
          finishRestore(false);
          return;
        }

        // Auf den anfänglichen Seitenaufbau warten.
        // Die Suchergebnisse nicht vorzeitig durchscrollen.
        if (
          productCount() === 0 &&
          Date.now() - startedAt < 5000
        ) {
          restoreTimer = setTimeout(
            restoreStep,
            STEP_DELAY
          );
          return;
        }

        showOverlay();

        // Kontrolliert in Richtung der Zielposition
        // scrollen und Searchanise selbst nachladen lassen.
        if (window.scrollY < targetY) {
          window.scrollBy({
            top: Math.min(
              STEP_SIZE,
              targetY - window.scrollY
            ),
            left: 0,
            behavior: 'instant'
          });
        }

        restoreTimer = setTimeout(
          restoreStep,
          STEP_DELAY
        );
      };

      restoreTimer = setTimeout(
        restoreStep,
        INITIAL_DELAY
      );

      // Escape bricht eine laufende Wiederherstellung ab.
      window.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && restoring) {
          finishRestore(false);
        }
      });
    }

    // ------------------------------------------------
    // URL UND SCROLLPOSITION SPEICHERN
    // ------------------------------------------------

    saveCurrentUrl();

    if (!restoring) {
      saveScrollPosition();
    }

    // Filterbedingte URL-Aenderungen durch Searchanise.
    for (const method of ['pushState', 'replaceState']) {
      const original = history[method];

      history[method] = function (...args) {
        const result = original.apply(this, args);
        saveCurrentUrl();
        return result;
      };
    }

    window.addEventListener(
      'popstate',
      saveCurrentUrl
    );

    window.addEventListener(
      'scroll',
      () => {
        if (restoring) return;

        clearTimeout(scrollTimer);

        scrollTimer = setTimeout(
          saveScrollPosition,
          150
        );
      },
      { passive: true }
    );

    window.addEventListener('pagehide', () => {
      clearTimeout(restoreTimer);

      if (!restoring) {
        saveCurrentUrl();
        saveScrollPosition();
      }
    });
  }

  // --------------------------------------------------
  // WARENKORB
  // --------------------------------------------------

  if (pageType === 'cart') {

    const button = document.getElementById(
      'ofs-continue-shopping'
    );

    const savedUrl = sessionStorage.getItem(URL_KEY);

    if (button && savedUrl && isValidShoppingUrl(savedUrl)) {

      button.href = savedUrl;

      button.addEventListener('click', () => {

        // Im Theme-Editor konfigurierbar.
        const enableScrollRestore =
          button.dataset.restoreScroll === 'true';

        if (enableScrollRestore) {
          sessionStorage.setItem(
            RESTORE_KEY,
            'true'
          );
        } else {
          sessionStorage.removeItem(
            RESTORE_KEY
          );
        }
      });

    } else {
      // Kein gueltiger Shopping-Kontext vorhanden.
      sessionStorage.removeItem(RESTORE_KEY);
    }
  }
})();
